import { useState, type ReactNode } from "react"

import { I18nextProvider } from "react-i18next"

import { InitialLanguageSelector, useLanguageController } from "@/features/user/language"

import { isSupportedLanguage } from "@/entities/user/language"

import { i18n } from "@/shared/i18n"
import { isAdOnboardingTab, parseStartParams } from "@/shared/router"
import { WebApp } from "@/shared/telegram"

import { useAppContext } from "../app-provider"
import { useLaunchAuthRejected } from "../clone-launch-gate"

interface LanguageProviderProps {
  children: ReactNode
}

/**
 * An ad visitor whose Telegram language is supported reads the app in it without the first-visit picker. Read from
 * the launch parameters, which the onboarding page's tab consumption does not touch, so it lasts the session.
 */
const resolveAdVisitorLanguage = () => {
  if (!isAdOnboardingTab(parseStartParams().tabParam)) return null
  const telegramLanguage = WebApp.initDataUnsafe.user?.language_code
  return isSupportedLanguage(telegramLanguage) ? telegramLanguage : null
}

export const LanguageProvider = (props: LanguageProviderProps) => {
  const { children } = props
  const { captchaMode } = useAppContext()
  const [adVisitorLanguage] = useState(resolveAdVisitorLanguage)
  const launchAuthRejected = useLaunchAuthRejected()
  // Centralized language resolution and application
  useLanguageController(adVisitorLanguage)

  return (
    <I18nextProvider i18n={i18n}>
      {children}
      {!captchaMode && !adVisitorLanguage && !launchAuthRejected && <InitialLanguageSelector />}
    </I18nextProvider>
  )
}
